/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import { _x } from '@web-stories-wp/i18n';
import PropTypes from 'prop-types';

const title = _x('Phone', 'sticker name', 'web-stories');

const PhoneInHand = ({ style }) => (
  <svg
    style={style}
    viewBox="0 0 36 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>{title}</title>
    <path
      d="M6.63249 31.6062C6.7549 27.2132 9.56206 16.4426 16.5677 14.0109C20.6833 12.5822 25.1679 11.9638 28.3332 12.0185L28.6446 15.7685L27.2741 15.8823L22.6 31.3034C22.6 35.3687 20.2372 36.1723 20.2372 39.6989L19.6869 49.4142L4.35262 48.1605C4.35262 48.1605 4.23974 48.1162 6.23159 37.1106C6.65439 33.9367 6.60924 32.4357 6.63249 31.6062Z"
      fill="#1FC1BE"
    />
    <path
      d="M22.2024 33.8086C20.8788 33.651 18.1749 33.3359 17.948 33.3359V33.0522H22.5333L22.2024 33.8086Z"
      fill="#1DAFAC"
    />
    <path
      d="M32.3604 19.5648C32.3604 22.0614 28.6751 21.274 28.1214 20.9742C27.5681 20.6744 27.7905 17.1925 27.7905 17.1925C27.7905 17.1925 32.3604 16.3889 32.3604 19.5648Z"
      fill="url(#paint0_linear)"
    />
    <path
      d="M32.0313 24.1909C31.8287 26.0089 28.3815 25.5051 27.8278 25.2054C26.4569 24.4632 27.4969 21.7545 27.4969 21.7545C27.4969 21.7545 32.351 21.3229 32.0313 24.1909Z"
      fill="url(#paint1_linear)"
    />
    <path
      d="M30.3923 27.7716C30.7185 29.9764 27.3318 29.6251 26.7786 29.3253C26.2253 29.0254 26.5742 25.4187 26.5742 25.4187C26.5742 25.4187 30.066 25.5669 30.3923 27.7716Z"
      fill="url(#paint2_linear)"
    />
    <path
      d="M31.8912 13.0398C32.8727 13.8864 32.555 16.4599 29.3878 16.4599C26.4633 17.0316 28.1114 12.7728 28.1114 12.7728C28.1114 12.7728 29.6241 12.6309 31.8912 13.0398Z"
      fill="url(#paint3_linear)"
    />
    <path
      d="M13.8221 17.5558L14.0439 17.9726C14.0248 17.994 10.6719 21.3714 9.62107 23.3179L9.33244 22.9977C9.55312 20.9034 13.7423 17.6451 13.8221 17.5558Z"
      fill="#1DAFAD"
    />
    <path
      d="M29.0417 2.23144C29.0417 1.60486 28.5338 1.09692 27.9072 1.09692H15.6167C14.9901 1.09692 14.4822 1.60486 14.4822 2.23144V31.3506C14.4822 31.9772 14.9901 32.4851 15.6167 32.4851H27.9072C28.5338 32.4851 29.0417 31.9772 29.0417 31.3506V2.23144Z"
      fill="#1D1D1D"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M27.9073 0.435059H15.6168C14.6247 0.435059 13.8204 1.23929 13.8204 2.23137V31.3506C13.8204 32.3426 14.6247 33.1469 15.6167 33.1469H27.9073C28.8994 33.1469 29.7036 32.3426 29.7036 31.3506V2.23137C29.7036 1.2393 28.8994 0.435059 27.9073 0.435059ZM27.9073 1.09686C28.5339 1.09686 29.0418 1.6048 29.0418 2.23137V31.3506C29.0418 31.9771 28.5339 32.4851 27.9073 32.4851H15.6167C14.9902 32.4851 14.4822 31.9771 14.4822 31.3506V2.23137C14.4822 1.6048 14.9902 1.09686 15.6168 1.09686H27.9073Z"
      fill="white"
    />
    <path
      d="M20.1543 1.94681C20.8446 1.95225 22.9608 1.96967 23.634 1.9703C23.7472 1.9706 23.8376 2.06358 23.8348 2.17704L23.8346 2.18288C23.8318 2.29291 23.7416 2.38011 23.6318 2.37922L20.1518 2.34984C20.0479 2.3491 19.9615 2.26877 19.9529 2.16528C19.9426 2.04647 20.0358 1.94592 20.1543 1.94681Z"
      fill="#74747C"
    />
    <path
      d="M15.1201 41.6529C15.1191 41.6533 15.1186 41.6537 15.1176 41.6541C15.1176 41.6541 15.1268 41.6635 15.1268 41.6639L15.1261 41.6702L15.115 41.6597L15.1173 41.6517C15.1183 41.6518 15.1192 41.6524 15.1201 41.6529Z"
      fill="white"
    />
    <path
      d="M15.1175 41.6519C15.1166 41.6517 15.1157 41.6507 15.1143 41.6505C15.1153 41.6506 15.1167 41.6508 15.1176 41.6509L15.1175 41.6519Z"
      fill="white"
    />
    <path
      d="M15.1002 41.6661L15.0585 41.6581L15.0609 41.6496C15.0613 41.6501 15.0903 41.6621 15.1002 41.6661Z"
      fill="white"
    />
    <path
      d="M15.6056 16.6693C16.5692 15.7262 18.0967 13.7074 19.3933 15.5162C21.5407 18.2565 16.6903 22.4049 15.3009 24.7327C13.741 28.0417 15.2355 29.999 15.8846 31.306C17.0523 33.6574 19.3233 33.4562 19.3233 33.4562L7.90046 36.2313C7.82932 35.5309 7.4374 31.9248 7.4374 31.9248C7.4374 31.9248 7.52051 25.3082 9.24398 22.8114C10.9679 20.3145 14.6419 17.6124 15.6056 16.6693Z"
      fill="#1FC1BE"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.2476 16.9362C18.8045 16.6543 19.125 16.078 19.2851 15.6697L19.109 15.6007C18.9544 15.995 18.6563 16.5174 18.1622 16.7675C17.6789 17.0122 16.9756 17.0112 15.9715 16.2917L15.8613 16.4454C16.9004 17.1899 17.6801 17.2235 18.2476 16.9362Z"
      fill="#1DAFAC"
    />
    <path
      d="M5.51554 35.8887L21.3042 40.3322L20.6424 44.2084L4.28648 40.8049L5.51554 35.8887Z"
      fill="white"
    />
    <path
      d="M4.02441 38.2522L21.3042 43.0997L19.8861 49.7864L0.977512 48.3683L4.02441 38.2522Z"
      fill="#4D5777"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="32.242"
        y1="18.5259"
        x2="27.7638"
        y2="19.0407"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#23D4D1" />
        <stop offset="1" stopColor="#1DAFAC" />
      </linearGradient>
      <linearGradient
        id="paint1_linear"
        x1="31.9129"
        y1="23.152"
        x2="27.4347"
        y2="23.6668"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#23D4D1" />
        <stop offset="1" stopColor="#1DAFAC" />
      </linearGradient>
      <linearGradient
        id="paint2_linear"
        x1="30.3231"
        y1="27.16"
        x2="26.4007"
        y2="27.6109"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#23D4D1" />
        <stop offset="1" stopColor="#1DAFAC" />
      </linearGradient>
      <linearGradient
        id="paint3_linear"
        x1="32.354"
        y1="13.9801"
        x2="27.8388"
        y2="14.4992"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#23D4D1" />
        <stop offset="1" stopColor="#1EB3B0" />
      </linearGradient>
    </defs>
  </svg>
);
PhoneInHand.propTypes = {
  style: PropTypes.object,
};

export default {
  aspectRatio: 36 / 51,
  svg: PhoneInHand,
  title,
};
